/**
 * Cart drawer — lightweight REST cart ops.
 */

let busy = false;

function getConfig() {
	return window.doneCommerce || {};
}

async function apiFetch(path, options = {}) {
	const config = getConfig();
	const response = await fetch(`${config.restUrl}${path}`, {
		credentials: 'same-origin',
		...options,
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': config.nonce,
			...(options.headers || {}),
		},
	});

	const payload = await response.json().catch(() => null);

	if (!response.ok) {
		const error = new Error(payload?.message || 'Request failed');
		error.status = response.status;
		throw error;
	}

	return payload;
}

function formatMoney(amount) {
	const config = getConfig();
	const value = Number.parseFloat(String(amount ?? '0'));
	const formatted = Number.isFinite(value) ? value.toFixed(2) : '0.00';
	return `${config.currency || '$'}${formatted}`;
}

function escapeHtml(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function updateCartCounts(count) {
	document.querySelectorAll('[data-cart-count]').forEach((el) => {
		el.textContent = String(count);
		el.dataset.count = String(count);
		el.hidden = Number(count) <= 0;
	});
}

function renderCartItems(items) {
	const container = document.querySelector('[data-cart-items]');
	if (!container) {
		return;
	}

	if (!items?.length) {
		container.innerHTML = `<p class="dc-cart-drawer__empty">${escapeHtml(getConfig().i18n?.cartEmpty || 'Your cart is empty.')}</p>`;
		return;
	}

	container.innerHTML = items
		.map((item) => {
			const key = escapeHtml(item.key);
			const name = escapeHtml(item.name);
			const qty = Number(item.quantity) || 1;
			const thumb = item.thumbnail
				? `<img class="dc-cart-item__thumb" src="${escapeHtml(item.thumbnail)}" alt="" width="64" height="64" loading="lazy">`
				: '<span class="dc-cart-item__thumb dc-cart-item__thumb--empty" aria-hidden="true"></span>';
			const price = item.line_total_html || formatMoney(item.line_total);

			return `
		<div class="dc-cart-item" data-cart-key="${key}">
			${thumb}
			<div class="dc-cart-item__details">
				<div class="dc-cart-item__title">${name}</div>
				<div class="dc-cart-item__qty" role="group" aria-label="${escapeHtml(getConfig().i18n?.quantity || 'Quantity')}">
					<button type="button" class="dc-cart-item__qty-btn" data-cart-qty="-1" aria-label="${escapeHtml(getConfig().i18n?.decreaseQty || 'Decrease quantity')}">−</button>
					<span class="dc-cart-item__qty-value" data-cart-qty-value>${qty}</span>
					<button type="button" class="dc-cart-item__qty-btn" data-cart-qty="1" aria-label="${escapeHtml(getConfig().i18n?.increaseQty || 'Increase quantity')}">+</button>
				</div>
				<button type="button" class="dc-cart-item__remove" data-cart-remove>${escapeHtml(getConfig().i18n?.removeItem || 'Remove')}</button>
			</div>
			<div class="dc-cart-item__price">${price}</div>
		</div>`;
		})
		.join('');
}

async function loadCartRecommendations(items) {
	const slot = document.querySelector('[data-cart-recommendations]');
	const grid = document.querySelector('[data-cart-recommendations-grid]');

	if (!slot || !grid) {
		return;
	}

	if (!items?.length) {
		slot.setAttribute('hidden', '');
		grid.innerHTML = '';
		return;
	}

	const productId = Number(items[0]?.product_id);
	if (!productId) {
		slot.setAttribute('hidden', '');
		return;
	}

	try {
		const config = getConfig();
		const response = await fetch(
			`${config.restUrl}done-commerce/v1/recommendations?product_id=${productId}&context=cart_drawer&limit=2`,
			{
				credentials: 'same-origin',
				headers: { 'X-WP-Nonce': config.nonce },
			}
		);

		if (!response.ok) {
			slot.setAttribute('hidden', '');
			return;
		}

		const data = await response.json();
		const products = (data.products || []).slice(0, 2);

		if (!products.length) {
			slot.setAttribute('hidden', '');
			return;
		}

		const { renderProductCards } = await import('./product-card-render.js');
		renderProductCards(grid, products);
		slot.removeAttribute('hidden');
	} catch {
		slot.setAttribute('hidden', '');
	}
}

async function refreshCart() {
	const data = await apiFetch('done-commerce/v1/cart');
	updateCartCounts(data.count ?? 0);

	const subtotal = document.querySelector('[data-cart-subtotal]');
	if (subtotal) {
		if (data.subtotal_html) {
			subtotal.innerHTML = data.subtotal_html;
		} else {
			subtotal.textContent = formatMoney(data.subtotal);
		}
	}

	renderCartItems(data.items || []);
	await loadCartRecommendations(data.items || []);
	return data;
}

function setDrawerBusy(isBusy) {
	busy = isBusy;
	const drawer = document.getElementById('dc-cart-drawer');
	drawer?.classList.toggle('is-busy', isBusy);
	drawer?.querySelectorAll('[data-cart-qty], [data-cart-remove]').forEach((el) => {
		el.disabled = isBusy;
	});
}

async function mutateItem(key, nextQty) {
	if (!key || busy) {
		return;
	}

	const encoded = encodeURIComponent(key);
	setDrawerBusy(true);

	try {
		if (nextQty <= 0) {
			await apiFetch(`done-commerce/v1/cart/items/${encoded}`, { method: 'DELETE' });
		} else {
			await apiFetch(`done-commerce/v1/cart/items/${encoded}`, {
				method: 'POST',
				body: JSON.stringify({ quantity: nextQty }),
			});
		}
		await refreshCart();
		announce(getConfig().i18n?.cartUpdated || 'Cart updated');
	} catch {
		announce(getConfig().i18n?.cartUpdateFailed || 'Unable to update cart');
		try {
			await refreshCart();
		} catch {
			// Ignore secondary refresh failures.
		}
	} finally {
		setDrawerBusy(false);
	}
}

function announce(message) {
	const live = document.querySelector('[data-cart-live]');
	if (live) {
		live.textContent = '';
		window.requestAnimationFrame(() => {
			live.textContent = message;
		});
	}
}

function onEscape(e) {
	if (e.key !== 'Escape') {
		return;
	}
	const drawer = document.getElementById('dc-cart-drawer');
	if (drawer && !drawer.hidden) {
		closeDrawer(drawer, document.querySelector('[data-cart-open]'));
	}
}

function openDrawer(drawer, trigger, options = {}) {
	drawer.hidden = false;
	document.body.classList.add('no-scroll');
	drawer.querySelector('.dc-cart-drawer__close')?.focus();
	window.requestAnimationFrame(() => {
		drawer.classList.add('is-open');
	});

	refreshCart()
		.then(() => {
			if (options.announce) {
				announce(options.announce);
			}
		})
		.catch(() => {
			announce(getConfig().i18n?.cartUpdateFailed || 'Unable to update cart');
		});
}

function closeDrawer(drawer, trigger) {
	drawer.hidden = true;
	drawer.classList.remove('is-open');
	document.body.classList.remove('no-scroll');
	trigger?.focus();
}

export function openCartDrawer(options = {}) {
	const drawer = document.getElementById('dc-cart-drawer');
	const openBtn = document.querySelector('[data-cart-open]');
	if (!drawer) {
		return;
	}
	openDrawer(drawer, openBtn, options);
}

export function announceCart(message) {
	announce(message);
}

export function initCartDrawer() {
	const drawer = document.getElementById('dc-cart-drawer');
	const openBtn = document.querySelector('[data-cart-open]');

	if (!drawer) {
		return;
	}

	openBtn?.addEventListener('click', () => openDrawer(drawer, openBtn));

	drawer.querySelectorAll('[data-cart-close]').forEach((btn) => {
		btn.addEventListener('click', () => closeDrawer(drawer, openBtn));
	});

	document.addEventListener('keydown', onEscape);

	drawer.addEventListener('click', (e) => {
		const item = e.target.closest('.dc-cart-item');
		if (!item || busy) {
			return;
		}

		const key = item.dataset.cartKey;
		if (!key) {
			return;
		}

		if (e.target.closest('[data-cart-remove]')) {
			e.preventDefault();
			mutateItem(key, 0);
			return;
		}

		const qtyBtn = e.target.closest('[data-cart-qty]');
		if (!qtyBtn) {
			return;
		}

		e.preventDefault();
		const delta = Number.parseInt(qtyBtn.getAttribute('data-cart-qty') || '0', 10);
		const current = Number.parseInt(item.querySelector('[data-cart-qty-value]')?.textContent || '1', 10);
		mutateItem(key, Math.max(0, current + delta));
	});

	refreshCart().catch(() => {
		// Cart may be unavailable outside WooCommerce context.
	});
}

export { refreshCart, apiFetch };
